import { RenderPass } from "@/core/pipeline/render-pass"
import { Renderer } from "@/core/pipeline/renderer"
import { BrushStroke, computeBrushTextureSize } from "@/core/utils/brushing"
import { HealingRenderer } from "@/core/healing-renderer"
import { DualBuffers } from "../dual-buffers"
import { BrushMaskCacheEntry, MaskPainter } from "@/core/utils/mask-painter"
import { equals } from "@/utils/math"
import { vec2 } from "gl-matrix"
import { HealingAiInputNormalization, HealingItem, Rectangle } from "@/core/types"

export class HealingPass extends RenderPass {
	private maskCache: Record<string, BrushMaskCacheEntry> = {}

	public healingRenderer: HealingRenderer

	private lastPhotoId = ""
	private lastLumDenoise = 0.0
	private lastColorDenoise = 0.0
	private lastDenoiseEnabled = false

	private maskPainter: MaskPainter

	// temporary for healing drawing strokes
	healingCurrentStroke: BrushStroke | null = null

	// disable healing pass
	enabled = true

	constructor(renderer: Renderer, id: string) {
		super(renderer, id)
		this.healingRenderer = new HealingRenderer(renderer)
		this.maskPainter = new MaskPainter(renderer, true)
	}

	hasHealingAdjustment(healingMasks: HealingItem[] | undefined) {
		return healingMasks && healingMasks.length > 0
	}

	private getMaskTexture() {
		const cache = this.maskCache["healing"] ?? this.createMaskCache()

		return cache.texture
	}

	private createMaskCache(): BrushMaskCacheEntry {
		const renderer = this.renderer

		const [width, height] = computeBrushTextureSize(
			renderer.photo.imageState.originalImageSize[0],
			renderer.photo.imageState.originalImageSize[1],
		)

		const texture = renderer.backend.createTexture({
			label: "healing.maskTexture",
			format: "r8unorm",
			width,
			height,
		})

		return {
			texture,
			strokes: [],
		}
	}

	renderHealingMask(rwBuffers: DualBuffers) {
		if (!this.healingCurrentStroke) return

		const mask = this.getMaskTexture()

		this.maskPainter.drawMask([this.healingCurrentStroke], mask, {
			isAdditive: false,
		})

		if (!mask) {
			return
		}

		const { renderer } = this
		const cropMatrix = renderer.cropMatrix
		const perspectiveMatrix = renderer.perspectiveMatrix
		const shaders = renderer.shaders

		renderer.setRenderTarget(rwBuffers.writeBuffer)
		rwBuffers.readBuffer.bind(0)
		mask.bind(1)

		shaders.brushMask.render({
			inputTexture: 0,
			maskTexture: 1,
			cropMatrix: cropMatrix,
			perspectiveMatrix: perspectiveMatrix,
			inverted: false,
			subtract: false,
			channelIndex: 3,
		})

		rwBuffers.swapBuffers()
	}

	render(rwBuffers: DualBuffers, outputSize: vec2) {
		const { renderer } = this
		const { photo } = renderer
		const { healingMasks, colorNoiseReduction, luminanceNoiseReduction } = photo.adjustments

		if (this.lastPhotoId !== photo.id) {
			this.resetPhoto()
		}

		const textureChanged =
			!equals(this.lastColorDenoise, colorNoiseReduction, 0.02) ||
			!equals(this.lastLumDenoise, luminanceNoiseReduction, 0.02) ||
			this.lastDenoiseEnabled !== renderer.denoiseEnabled

		if (textureChanged) {
			this.lastColorDenoise = colorNoiseReduction
			this.lastLumDenoise = luminanceNoiseReduction
			this.lastDenoiseEnabled = renderer.denoiseEnabled
		}

		const needCompute = this.healingRenderer.update(healingMasks ?? [], textureChanged)

		if (needCompute) {
			// stable healing compute -> normal image transform -> fast healing composite
			this.healingRenderer.computeAndRender(rwBuffers, outputSize)
		} else {
			// Original transform pass
			rwBuffers.updateSize(outputSize[0], outputSize[1])
			renderer.transformPass.render(photo.texture, rwBuffers.writeBuffer)
			rwBuffers.swapBuffers()

			// Fast draw: directly copy healed patch to fully transformed space
			this.healingRenderer.fastRender(rwBuffers, outputSize)
		}
	}

	renderAiInput(
		rect: Rectangle,
		outputSize: vec2,
		replaceHealingId?: string,
		aiInputNormalization?: HealingAiInputNormalization,
	) {
		const { renderer } = this
		const { photo } = renderer
		let { healingMasks } = photo.adjustments

		if (replaceHealingId && healingMasks) {
			healingMasks = healingMasks.filter((mask) => mask.id !== replaceHealingId)
		}

		this.healingRenderer.update(healingMasks ?? [], false)

		// stable healing compute -> composite previous healings -> effective exposure + toSrgb
		return this.healingRenderer.renderAiInput(rect, outputSize, aiInputNormalization)
	}

	clearMaskCache() {
		this.maskCache["healing"]?.texture.delete()
		this.maskCache = {}
	}

	dispose() {
		this.clearMaskCache()
		this.healingRenderer.dispose()
	}

	resetPhoto() {
		const { renderer } = this
		const { photo } = renderer
		const { colorNoiseReduction, luminanceNoiseReduction } = photo.adjustments

		if (this.lastPhotoId !== photo.id) {
			this.dispose()

			this.lastPhotoId = photo.id
			this.lastColorDenoise = colorNoiseReduction
			this.lastLumDenoise = luminanceNoiseReduction
			this.lastDenoiseEnabled = renderer.denoiseEnabled
		}
	}
}
